import {
  SocialListeningAnalysisDetails,
  SocialListeningHighlight,
  SocialListeningHighlightLink,
  SocialListeningSourceBreakdown,
  SocialListeningSourcePost,
} from "src/services/SocialListeningAPIs";

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const toNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const capitalizeSource = (name: string) => {
  const trimmed = (name || "").trim();
  if (!trimmed) {return "";}
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const calcPct = (count: number, total: number) => (total > 0 ? (count / total) * 100 : 0);

export const stripMarkdownBullet = (value: string) =>
  value.replace(/^\s*[-*+]\s+/, "").trim();

export const normalizePost = (raw: unknown): SocialListeningSourcePost | null => {
  const post = asRecord(raw);
  if (!post) {return null;}

  const commentsRaw = post.recent_comments ?? post.comments ?? post.sample_comments;
  const recentComments = asArray<unknown>(commentsRaw)
    .map(comment => {
      if (typeof comment === "string") {return comment;}
      const row = asRecord(comment);
      return String(row?.text ?? row?.comment ?? row?.body ?? "").trim();
    })
    .filter(Boolean);

  const details = String(
    post.post_details ??
      post.post_text ??
      post.caption ??
      post.title ??
      post.text ??
      post.excerpt ??
      "",
  ).trim();

  if (!details && !recentComments.length) {return null;}

  const engagement = asRecord(post.engagement);
  const likesRaw =
    post.likes_count ??
    post.post_likes ??
    post.likes ??
    post.like_count ??
    engagement?.likes ??
    engagement?.likes_count;

  return {
    post_id: String(post.post_id ?? post.id ?? ""),
    post_details: details || "—",
    post_url: String(post.post_url ?? post.url ?? post.permalink ?? "").trim() || undefined,
    type: String(post.type ?? post.media_type ?? post.post_type ?? "TEXT").toUpperCase(),
    recent_comments: recentComments,
    likes_count:
      likesRaw === undefined || likesRaw === null ? undefined : toNumber(likesRaw),
    engagement: engagement
      ? {
          likes:
            engagement.likes !== undefined
              ? toNumber(engagement.likes)
              : undefined,
          comments:
            engagement.comments !== undefined
              ? toNumber(engagement.comments)
              : undefined,
          shares:
            engagement.shares !== undefined
              ? toNumber(engagement.shares)
              : undefined,
        }
      : undefined,
  };
};

const normalizePosts = (source: Record<string, unknown>): SocialListeningSourcePost[] => {
  const candidates = source.posts ?? source.post_list ?? source.items ?? source.entries;
  return asArray<unknown>(candidates)
    .map(normalizePost)
    .filter((post): post is SocialListeningSourcePost => Boolean(post));
};

const attachSampleExcerpts = (
  sources: SocialListeningSourceBreakdown[],
  excerpts: unknown,
): SocialListeningSourceBreakdown[] => {
  const rows = asArray<unknown>(excerpts);
  if (!rows.length) {return sources;}

  return sources.map(source => {
    if (source.posts?.length) {return source;}

    const sourceKey = source.source_name.toLowerCase();
    const matched = rows
      .map(row => {
        const record = asRecord(row);
        if (!record) {return null;}
        const rowSource = String(record.source_name ?? record.source ?? record.platform ?? "")
          .toLowerCase()
          .trim();
        if (rowSource && rowSource !== sourceKey) {return null;}
        return normalizePost(record);
      })
      .filter((post): post is SocialListeningSourcePost => Boolean(post));

    return matched.length ? { ...source, posts: matched } : source;
  });
};

const normalizeSourceBreakdown = (
  rawSources: unknown,
  samplePostExcerpts?: unknown,
): SocialListeningSourceBreakdown[] => {
  const sources = asArray<unknown>(rawSources)
    .map(item => {
      const source = asRecord(item);
      if (!source) {return null;}

      const sentiment = asRecord(source.sentiment_data) ?? {};
      const totalCount = toNumber(
        sentiment.total_count ??
          source.total_count ??
          toNumber(source.total_comments) + toNumber(source.total_posts),
      );
      const positiveCount = toNumber(sentiment.positive_count);
      const neutralCount = toNumber(sentiment.neutral_count);
      const negativeCount = toNumber(sentiment.negative_count);

      const positivePct = toNumber(
        sentiment.positive_percentage,
        calcPct(positiveCount, totalCount),
      );
      const neutralPct = toNumber(
        sentiment.neutral_percentage,
        calcPct(neutralCount, totalCount),
      );
      const negativePct = toNumber(
        sentiment.negative_percentage,
        calcPct(negativeCount, totalCount),
      );

      const sourceName = capitalizeSource(String(source.source_name ?? source.platform ?? ""));

      const normalized: SocialListeningSourceBreakdown = {
        source_name: sourceName,
        total_posts: toNumber(source.total_posts),
        total_comments: toNumber(source.total_comments),
        sentiment_data: {
          positive_count: positiveCount,
          positive_percentage: positivePct,
          neutral_count: neutralCount,
          neutral_percentage: neutralPct,
          negative_count: negativeCount,
          negative_percentage: negativePct,
          total_count: totalCount,
        },
        key_themes: asArray<string>(source.key_themes),
        top_positive_points: asArray<string>(source.top_positive_points),
        top_negative_points: asArray<string>(source.top_negative_points),
        posts: normalizePosts(source),
      };

      return normalized;
    })
    .filter((source): source is SocialListeningSourceBreakdown => Boolean(source));

  return attachSampleExcerpts(sources, samplePostExcerpts);
};

const mergeSourceBreakdownLists = (
  primary: SocialListeningSourceBreakdown[],
  secondary: SocialListeningSourceBreakdown[],
): SocialListeningSourceBreakdown[] => {
  if (!secondary.length) {return primary;}
  if (!primary.length) {return secondary;}

  return primary.map(source => {
    const match = secondary.find(
      row => row.source_name.toLowerCase() === source.source_name.toLowerCase(),
    );
    if (match?.posts?.length && !source.posts?.length) {
      return { ...source, posts: match.posts };
    }
    return source;
  });
};

const normalizeHighlights = (raw: unknown): SocialListeningHighlight[] => {
  const highlights = asArray<unknown>(raw);
  return highlights
    .map(item => {
      if (typeof item === "string") {
        return stripMarkdownBullet(item);
      }
      const row = asRecord(item);
      if (!row) {return "";}
      if (typeof row.text === "string") {
        return {
          text: stripMarkdownBullet(row.text),
          links: asArray<SocialListeningHighlightLink>(row.links),
        };
      }
      return "";
    })
    .filter(item => (typeof item === "string" ? item.length > 0 : item.text.length > 0));
};

const normalizeStringList = (raw: unknown): string[] =>
  asArray<unknown>(raw)
    .map(item => (typeof item === "string" ? stripMarkdownBullet(item) : ""))
    .filter(Boolean);

export const normalizeAnalysisDetails = (payload: unknown): SocialListeningAnalysisDetails | null => {
  const root = asRecord(payload);
  if (!root) {return null;}

  const data = asRecord(root.data) ?? root;
  const report = asRecord(data.report) ?? {};

  const competitiveBatch = asRecord(data.competitive_batch);
  const rawLlmComparison = asRecord(competitiveBatch?.llm_comparison);
  const llmComparison = rawLlmComparison
    ? Object.fromEntries(
        Object.entries(rawLlmComparison).filter(
          ([, value]) =>
            typeof value === "string" ||
            (Array.isArray(value) && value.every(item => typeof item === "string")),
        ),
      ) as Record<string, string | string[]>
    : undefined;

  const merged: Record<string, unknown> = {
    ...report,
    ...data,
    overall_sentiment: data.overall_sentiment ?? report.overall_sentiment,
    source_breakdown: data.source_breakdown ?? report.source_breakdown,
    narrative_summary: data.narrative_summary ?? report.narrative_summary,
    key_insights: data.key_insights ?? report.key_insights ?? report.highlights,
    recommended_actions: data.recommended_actions ?? report.recommended_actions,
    executive_summary:
      data.executive_summary ?? report.executive_summary ?? report.summary,
    highlights: data.highlights ?? report.highlights,
  };

  const overallRaw = asRecord(merged.overall_sentiment) ?? {};
  const totalCount = toNumber(
    overallRaw.total_count ?? overallRaw.total_texts ?? overallRaw.total_items,
  );
  const positiveCount = toNumber(overallRaw.positive_count);
  const neutralCount = toNumber(overallRaw.neutral_count);
  const negativeCount = toNumber(overallRaw.negative_count);

  const sourceBreakdown = mergeSourceBreakdownLists(
    normalizeSourceBreakdown(data.source_breakdown, data.sample_post_excerpts),
    normalizeSourceBreakdown(report.source_breakdown, report.sample_post_excerpts),
  );
  const totalPostsFromSources = sourceBreakdown.reduce((sum, source) => sum + source.total_posts, 0);

  const narrativeSummary = String(merged.narrative_summary ?? "").trim();
  const executiveSummary = String(merged.executive_summary ?? "").trim();

  return {
    analysis_id: toNumber(merged.analysis_id),
    analysis_type: String(merged.analysis_type ?? ""),
    company_name: String(merged.company_name ?? ""),
    status: String(merged.status ?? ""),
    sentiment_method: String(merged.sentiment_method ?? ""),
    translation_method: String(merged.translation_method ?? ""),
    data_quality_score: toNumber(merged.data_quality_score),
    sources_analyzed: asArray<string>(merged.sources_analyzed).map(capitalizeSource),
    overall_sentiment: {
      total_texts: totalCount,
      total_posts: toNumber(overallRaw.total_posts, totalPostsFromSources),
      positive_count: positiveCount,
      positive_percentage: toNumber(
        overallRaw.positive_percentage,
        calcPct(positiveCount, totalCount),
      ),
      neutral_count: neutralCount,
      neutral_percentage: toNumber(
        overallRaw.neutral_percentage,
        calcPct(neutralCount, totalCount),
      ),
      negative_count: negativeCount,
      negative_percentage: toNumber(
        overallRaw.negative_percentage,
        calcPct(negativeCount, totalCount),
      ),
    },
    narrative_summary: narrativeSummary,
    executive_summary: executiveSummary || narrativeSummary,
    key_insights: normalizeStringList(merged.key_insights),
    highlights: normalizeHighlights(merged.highlights ?? merged.key_insights),
    recommended_actions: normalizeStringList(merged.recommended_actions),
    source_breakdown: sourceBreakdown,
    analysis_date: String(merged.analysis_date ?? ""),
    completed_at: String(merged.completed_at ?? ""),
    created_at: String(merged.created_at ?? ""),
    llm_comparison: llmComparison,
  };
};
